import React, { useState, useEffect, lazy } from 'react'

import { PAYPAL_THEME, ThemeWrapper } from '@paypalcorp/pp-react'
import { arrayOf, bool, element, number, string } from 'prop-types'

// components
import sessionTimerHoc from '../../components/sessionTimerHoc'
import PrivacyModal from '../../components/PrivacyModal'
import MerchantSubHeader from './merchant-sub-header'
// utils
import { fptiCustomSetup } from '../../utils/fpti'
import serverData from '../../utils/server-data'
import { pageSpec } from '../../config/fptiConstants'

const DWSubHeader = lazy(() => import('./dw-sub-header'))
const PrivacyHub = lazy(() => import('../privacyhub'))
const Settings = lazy(() => import('../settings'))
const DSRZettleHome = lazy(() => import('../dsr'))
const ReTriggerSMS = lazy(() => import('../retriggersms'))

const showHeaderFooter = () => {
  const header = document.getElementById('pnw-globalnav-header')
  const footer = document.getElementById('pnw-globalnav-footer')
  if (header && footer) {
    header.style.display = 'block'
    footer.style.display = 'block'
  }
}

function componentSelection(
  selectedComponent,
  onSectionClick,
  props,
  modalComponent,
  keys,
) {
  let selectedComponentToRender = null
  switch (selectedComponent) {
    case 'merchant':
      selectedComponentToRender = (
        <div>
          <MerchantSubHeader onSectionClick={onSectionClick} /> {modalComponent}
        </div>
      )
      break
    case 'privacyhub':
      selectedComponentToRender = (
        <>
          <PrivacyHub onSectionClick={onSectionClick} {...props} />{' '}
          {modalComponent}
        </>
      )
      break
    case 'zettle':
      selectedComponentToRender = (
        <div className="zettleContainer">
          <DSRZettleHome
            onSectionClick={onSectionClick}
            keys={keys}
            {...props}
          />{' '}
        </div>
      )
      break
    case 'retriggersms':
      selectedComponentToRender = (
        <div>
          <ReTriggerSMS keys={keys} {...props} />
        </div>
      )
      break
    case 'settings':
      selectedComponentToRender = (
        <>
          <Settings onSectionClick={onSectionClick} /> {modalComponent}
        </>
      )
      break
    default:
      selectedComponentToRender = (
        <div>
          <DWSubHeader onSectionClick={onSectionClick} /> {modalComponent}
        </div>
      )
  }
  return selectedComponentToRender
}

const Home = (props) => {
  const {
    selectedComponent,
    pageName = 'home',
    keys = {},
    elmoFlags = {},
  } = serverData

  const {
    children = null,
    isFlow = false,
    showBackButton = true,
    backUrl = '',
    closeUrl = '',
    domid,
    showPPlogo = false,
    hideCloseBtn = true,
    slides = [],
    redirect = '',
    transitionOnClose = true,
    noPadding = true,
  } = props
  const { tracking = {} } = elmoFlags
  const { xe = '', xt = '' } = tracking

  const [modalInfo, setModalInfo] = useState({})
  const [position, setPosition] = useState(0)
  const [modalParams, setModalParams] = useState({})
  const navigateToUrl = (url) => {
    window.location.href = url
  }
  const resetState = () => {
    setModalInfo({})
    return closeUrl ? navigateToUrl(closeUrl) : setPosition(0)
  }

  const onBackButtonClick = (event) => {
    event.preventDefault()
    const updatedbackUrl = backUrl || closeUrl
    if (slides.length === position + 1) {
      return navigateToUrl(updatedbackUrl)
    }
    return position && position > 0
      ? setPosition(position - 1)
      : navigateToUrl(updatedbackUrl)
  }
  const setModalStore = (param) => {
    Object.assign(modalParams, param)
  }
  const onCloseModal = () => resetState()

  const onSectionClick = (modal) => setModalInfo(modal)

  useEffect(() => {
    setPosition(props.position)
    setModalParams({})
    fptiCustomSetup(
      pageSpec.landing_data_and_privacy_page({ xe, xt, pageName }),
    )
    showHeaderFooter()
  }, [])

  const childProps = {
    position,
    slides,
    redirect,
    modalParams,
    noPadding,
    updateModalStore: setModalStore,
    onModalNavigate: setPosition,
    ...modalInfo,
  }
  const isModalOpen = isFlow || !!modalInfo.name
  const shouldShowBackBtn =
    showBackButton || (position > 0 && position < slides.length - 1)
  const modalComponent = (
    <PrivacyModal
      childProps={childProps}
      id={domid ?? modalInfo.name ?? ''}
      isFlow={isFlow}
      hideCloseBtn={hideCloseBtn}
      isModalOpen={isModalOpen}
      onBackButtonClick={onBackButtonClick}
      onCloseModal={onCloseModal}
      shouldShowBackBtn={shouldShowBackBtn}
      showPPlogo={showPPlogo}
      slides={slides}
      transitionOnClose={transitionOnClose}
    >
      {children}
    </PrivacyModal>
  )

  if (isFlow) {
    return modalComponent
  }

  const selectedComponentToRender = componentSelection(
    selectedComponent,
    onSectionClick,
    props,
    modalComponent,
    keys,
  )

  return (
    <div data-testid="home-page">
      <ThemeWrapper theme={PAYPAL_THEME}>
        {selectedComponentToRender}
      </ThemeWrapper>
    </div>
  )
}
Home.propTypes = {
  children: element.isRequired,
  isFlow: bool,
  showBackButton: bool,
  backUrl: string,
  closeUrl: string,
  domid: string,
  showPPlogo: bool,
  hideCloseBtn: bool,
  redirect: string,
  position: number,
  slides: arrayOf(element),
  transitionOnClose: bool,
  noPadding: bool,
}

export default sessionTimerHoc(Home)
